import { publicEnv } from './env';
import type { SiteConfig } from './schema';

/**
 * Branding, theme and outbound links.
 *
 * Every colour the interface uses is declared here and emitted as CSS custom
 * properties at startup (see `$lib/theme/applyTheme.ts`). Components reference
 * `var(--color-accent)` and friends, never a literal, so re-skinning the site , 
 * or shipping a light theme, is a change to this file alone.
 */
export const site: SiteConfig = {
	name: publicEnv.serverName ?? 'Citrus Meadow',
	tagline: 'Live maps for the Citrus Meadow Minecraft network',
	logo: 'Map',

	/**
	 * Left empty deliberately: the placeholder links this shipped with pointed at
	 * `example.com`, and a public site should not carry dead outbound links. Add real
	 * ones as `{ label, href, icon }`, using an icon name from the registry in
	 * `$lib/components/ui/Icon.svelte`.
	 */
	links: [],

	theme: {
		/** 'dark' | 'light' | 'system'. */
		colorScheme: 'dark',
		switchable: true,

		/**
		 * Both palettes are declared in full. The theme toggle swaps custom-property
		 * values on `:root`; no component knows which scheme is active.
		 *
		 * Neutral, cool and low-chroma: the world behind the chrome supplies the colour,
		 * so the one saturated token is the accent, a lantern aqua that reads as a
		 * waypoint against terrain.
		 */
		dark: {
			accent: '#4ec5d4',
			accentContrast: '#04191d',
			background: '#0b0e13',
			surface: '#12161d',
			surfaceMuted: '#1a1f28',
			border: '#252c37',
			text: '#e7eaf0',
			textMuted: '#98a2b3',
			danger: '#e5534b',
			success: '#3fb950',
			warning: '#d29922'
		},

		light: {
			accent: '#0e7c8c',
			accentContrast: '#ffffff',
			background: '#eef1f5',
			surface: '#ffffff',
			surfaceMuted: '#f4f6f9',
			border: '#d8dee7',
			text: '#111823',
			textMuted: '#5b6675',
			danger: '#c9332b',
			success: '#1a7f37',
			warning: '#9a6700'
		},

		radius: '0.75rem',
		fontSans: "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
		fontMono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"
	}
};
